/**
 * Auth events between this browser's tabs (T-155). Each tab keeps its own team, so a team switch
 * in one tab is announced here and the others offer to follow, never follow by themselves.
 * Signing out in one tab signs out every tab (decision 7 a): the browser has one sign-in.
 * Without BroadcastChannel (very old Safari) every call is a no-op: no banner appears, and the
 * other tabs stay signed in until their session token runs out.
 */

export type AuthEvent =
  | { type: 'team-switched'; userId: string; teamId: string; teamName: string }
  | { type: 'signed-out'; userId: string };

export type OtherTabTeam = { id: string; name: string };

const CHANNEL_NAME = 'jflo-auth';

let channel: BroadcastChannel | null | undefined;

function getChannel(): BroadcastChannel | null {
  if (channel === undefined) {
    try {
      channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);
    } catch {
      channel = null;
    }
  }
  return channel;
}

/** Closes this tab's channel; the next call opens a new one. Tests use it so Jest can exit. */
export function closeAuthChannel(): void {
  channel?.close();
  channel = undefined;
}

export function isAuthEvent(value: unknown): value is AuthEvent {
  if (!value || typeof value !== 'object') return false;
  const e = value as Record<string, unknown>;
  if (typeof e.userId !== 'string') return false;
  if (e.type === 'signed-out') return true;
  return e.type === 'team-switched' && typeof e.teamId === 'string' && typeof e.teamName === 'string';
}

/** Tells the other tabs. The posting tab never hears its own message. */
export function postAuthEvent(event: AuthEvent): void {
  try {
    getChannel()?.postMessage(event);
  } catch {
    // A closed or unavailable channel: the other tabs just aren't told.
  }
}

/** Subscribes to the other tabs' events; returns the unsubscribe. */
export function onAuthEvent(listener: (event: AuthEvent) => void): () => void {
  const ch = getChannel();
  if (!ch) return () => {};
  const handle = (message: MessageEvent) => {
    if (isAuthEvent(message.data)) listener(message.data);
  };
  ch.addEventListener('message', handle);
  return () => ch.removeEventListener('message', handle);
}

/**
 * What this tab's "you switched in another tab" notice should show after `event`: unchanged for
 * another person's event, gone once the other tab is back on this tab's team, else that team.
 */
export function otherTabTeamAfter(
  event: AuthEvent,
  tab: { userId?: string | null; teamId?: string | null },
  current: OtherTabTeam | null
): OtherTabTeam | null {
  if (event.type !== 'team-switched') return current;
  if (!tab.userId || event.userId !== tab.userId) return current;
  if (event.teamId === tab.teamId) return null;
  return { id: event.teamId, name: event.teamName };
}

/**
 * Whether `event` signs this tab out: any sign-out while this tab is signed in, whoever it was,
 * because the browser's one sign-in cookie is gone either way.
 */
export function signsOutThisTab(event: AuthEvent, tab: { userId?: string | null }): boolean {
  return event.type === 'signed-out' && Boolean(tab.userId);
}
